import { useState, useEffect } from "react";
import { Download, Share, Plus, CheckCircle2, ArrowRight, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

function detectPlatform() {
  const ua = navigator.userAgent;
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const isChrome = /chrome/i.test(ua) && !/edg/i.test(ua);
  const isSamsung = /samsungbrowser/i.test(ua);
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return { isIOS, isAndroid, isChrome, isSamsung, isStandalone };
}

export default function Install() {
  const [platform, setPlatform] = useState<ReturnType<typeof detectPlatform> | null>(null);
  const [installState, setInstallState] = useState<"idle" | "installing" | "done">("idle");
  const [hasDeferred, setHasDeferred] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());
    setHasDeferred(!!window.deferredInstallPrompt);
    const onPrompt = () => setHasDeferred(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  async function handleInstall() {
    const prompt = window.deferredInstallPrompt;
    if (!prompt) return;
    setInstallState("installing");
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") {
      window.deferredInstallPrompt = null;
      setInstallState("done");
    } else {
      setInstallState("idle");
    }
  }

  if (!platform) return null;

  const { isIOS, isAndroid, isChrome, isSamsung, isStandalone } = platform;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0b132b] px-6 py-12">
      <div className="w-full max-w-sm flex flex-col items-center gap-8">

        <div className="flex flex-col items-center gap-4">
          <img src="/icon-512.png" alt="Flight Deck" className="w-24 h-24 rounded-2xl shadow-2xl" />
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white tracking-tight">Flight Deck</h1>
            <p className="text-[#277ADF] text-sm font-medium mt-1">Canadian Aviation Exam Prep</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 w-full">
          {[
            { label: "Questions", value: "3,000+" },
            { label: "Subjects", value: "14" },
            { label: "Language", value: "EN / FR" },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-xl bg-white/5 border border-white/10 p-3 text-center">
              <div className="text-white font-bold text-lg leading-none">{value}</div>
              <div className="text-white/50 text-xs mt-1">{label}</div>
            </div>
          ))}
        </div>

        {isStandalone ? (
          <div className="w-full rounded-2xl bg-green-500/10 border border-green-500/30 p-6 flex flex-col items-center gap-3">
            <CheckCircle2 className="w-10 h-10 text-green-400" />
            <p className="text-green-300 font-semibold text-center">Already installed!</p>
            <p className="text-white/60 text-sm text-center">Flight Deck is on your home screen.</p>
            <Button
              className="w-full bg-[#277ADF] hover:bg-[#1e6ac9] text-white mt-2"
              onClick={() => window.location.href = "/"}
            >
              Open Flight Deck <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

        ) : isAndroid && (isChrome || isSamsung) && hasDeferred ? (
          <div className="w-full flex flex-col gap-4">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5 text-center">
              <Smartphone className="w-8 h-8 text-[#277ADF] mx-auto mb-3" />
              <p className="text-white font-semibold">Add to Home Screen</p>
              <p className="text-white/60 text-sm mt-2">
                Install Flight Deck for quick access — no App Store needed.
              </p>
            </div>
            <Button
              size="lg"
              className="w-full bg-[#277ADF] hover:bg-[#1e6ac9] text-white font-semibold h-14 text-base rounded-xl"
              onClick={handleInstall}
              disabled={installState === "installing" || installState === "done"}
            >
              {installState === "done" ? (
                <><CheckCircle2 className="w-5 h-5 mr-2" /> Installed!</>
              ) : (
                <><Download className="w-5 h-5 mr-2" /> Install App</>
              )}
            </Button>
          </div>

        ) : isIOS ? (
          <div className="w-full flex flex-col gap-4">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <p className="text-white font-semibold text-center mb-4">Add to Home Screen</p>
              <div className="flex flex-col gap-4">
                <Step number={1} icon={<Share className="w-5 h-5 text-[#277ADF]" />}>
                  Tap the <strong className="text-white">Share</strong> button at the bottom of Safari
                </Step>
                <Step number={2} icon={<Plus className="w-5 h-5 text-[#277ADF]" />}>
                  Scroll down and tap <strong className="text-white">Add to Home Screen</strong>
                </Step>
                <Step number={3} icon={<CheckCircle2 className="w-5 h-5 text-green-400" />}>
                  Tap <strong className="text-white">Add</strong> in the top right corner
                </Step>
              </div>
            </div>
            <p className="text-white/40 text-xs text-center">
              Must be opened in Safari for the Add to Home Screen option to appear.
            </p>
          </div>

        ) : (
          <div className="w-full rounded-2xl bg-white/5 border border-white/10 p-5 text-center">
            <Smartphone className="w-8 h-8 text-[#277ADF] mx-auto mb-3" />
            <p className="text-white font-semibold">Install on your device</p>
            <p className="text-white/60 text-sm mt-2">
              Open this link in <strong className="text-white">Chrome</strong> on Android or{" "}
              <strong className="text-white">Safari</strong> on iPhone, then use your browser's menu
              to add it to your home screen.
            </p>
            <div className="mt-4 bg-[#0b132b] rounded-lg px-4 py-2 border border-white/10 inline-block">
              <span className="text-[#277ADF] text-sm font-mono">flightdecktrainer.ca</span>
            </div>
          </div>
        )}

        <p className="text-white/30 text-xs text-center">
          Free to install · No App Store required · Works offline
        </p>
      </div>
    </div>
  );
}

function Step({ number, icon, children }: { number: number; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#277ADF]/20 border border-[#277ADF]/40 flex items-center justify-center text-[#277ADF] text-xs font-bold">
        {number}
      </div>
      <div className="flex items-center gap-2 text-white/70 text-sm pt-0.5">
        {icon}
        <span>{children}</span>
      </div>
    </div>
  );
}
